import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Inbox as InboxIcon, Check, CheckCircle, Upload, Clock, Users, 
  TrendingUp, Star, X, Filter, User, FolderKanban, Trash2,
  ChevronRight
} from 'lucide-react';
import { useNotifications, NOTIFICATION_CATEGORIES } from '../context/NotificationContext';

// Get relative time string
const getRelativeTime = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

// Get icon for notification type
const getNotificationIcon = (category) => {
  switch (category) {
    case 'COMPLETION':
      return <CheckCircle className="w-5 h-5" />;
    case 'UPLOAD':
      return <Upload className="w-5 h-5" />;
    case 'APPROVAL':
      return <Check className="w-5 h-5" />;
    case 'COLLABORATION':
      return <Users className="w-5 h-5" />;
    case 'DEADLINE':
      return <Clock className="w-5 h-5" />;
    case 'PROGRESS':
      return <TrendingUp className="w-5 h-5" />;
    case 'SOULFOOD':
      return <Star className="w-5 h-5" />;
    default:
      return <InboxIcon className="w-5 h-5" />;
  }
};

const CATEGORIES = [
  { id: 'ALL', name: 'All Notifications', icon: InboxIcon },
  { id: 'COMPLETION', name: 'Completions', icon: CheckCircle },
  { id: 'UPLOAD', name: 'Uploads & Assets', icon: Upload },
  { id: 'APPROVAL', name: 'Approvals & Reviews', icon: Check },
  { id: 'COLLABORATION', name: 'Collaboration', icon: Users },
  { id: 'DEADLINE', name: 'Deadlines', icon: Clock },
  { id: 'PROGRESS', name: 'Progress Updates', icon: TrendingUp },
  { id: 'SOULFOOD', name: 'Soul Food', icon: Star }
];

const Inbox = () => {
  const navigate = useNavigate();
  const { 
    notifications, 
    getUnreadCountByCategory, 
    markAsRead, 
    markAllAsRead,
    dismissNotification,
    dismissAllNotifications,
    clearAllNotifications 
  } = useNotifications();
  
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [personFilter, setPersonFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  // Get filtered notifications
  const filteredNotifications = notifications.filter(n => {
    if (selectedCategory !== 'ALL' && n.category !== selectedCategory) return false;
    if (personFilter && !n.person?.toLowerCase().includes(personFilter.toLowerCase())) return false;
    if (projectFilter && !n.project?.toLowerCase().includes(projectFilter.toLowerCase())) return false;
    return true;
  });

  // Get unique persons and projects for filters
  const uniquePersons = [...new Set(notifications.map(n => n.person).filter(Boolean))];
  const uniqueProjects = [...new Set(notifications.map(n => n.project).filter(Boolean))];

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.actions && notification.actions[0]?.navigate) {
      navigate(notification.actions[0].navigate);
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="inbox-page">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Inbox</h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>
          Activity feed of everything your organization accomplished
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left Column - Categories */}
        <div className="w-64 flex-shrink-0">
          <div 
            className="rounded-xl p-4"
            style={{ 
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}
          >
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B' }}>
              Categories
            </h3>
            <div className="space-y-1">
              {CATEGORIES.map((category) => {
                const Icon = category.icon;
                const unreadCount = getUnreadCountByCategory(category.id);
                const isActive = selectedCategory === category.id;
                const categoryColor = category.id !== 'ALL' 
                  ? NOTIFICATION_CATEGORIES[category.id]?.color 
                  : '#00C9A7';

                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all"
                    style={{
                      backgroundColor: isActive ? 'rgba(0, 201, 167, 0.1)' : 'transparent',
                      color: isActive ? '#00C9A7' : '#475569'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = '#F8FAFC';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    data-testid={`category-${category.id.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon 
                        className="w-4 h-4" 
                        style={{ color: isActive ? categoryColor : '#94A3B8' }}
                      />
                      <span className="font-medium">{category.name}</span>
                    </div>
                    {unreadCount > 0 && (
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ 
                          backgroundColor: categoryColor + '20',
                          color: categoryColor
                        }}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column - Notification Feed */}
        <div className="flex-1">
          {/* Bulk Actions */}
          <div 
            className="rounded-xl p-4 mb-4 flex items-center justify-between"
            style={{ 
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                style={{ color: '#475569' }}
                data-testid="mark-all-read"
              >
                <Check className="w-3.5 h-3.5" />
                Mark all read
              </button>
              <button
                onClick={dismissAllNotifications}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                style={{ color: '#475569' }}
                data-testid="dismiss-all"
              >
                <X className="w-3.5 h-3.5" />
                Dismiss all
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Person Filter */}
              <div className="relative">
                <select
                  value={personFilter}
                  onChange={(e) => setPersonFilter(e.target.value)}
                  className="pl-7 pr-3 py-1.5 rounded-lg text-xs font-medium appearance-none cursor-pointer"
                  style={{ 
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    color: '#475569'
                  }}
                  data-testid="filter-person"
                >
                  <option value="">All People</option>
                  {uniquePersons.map(person => (
                    <option key={person} value={person}>{person}</option>
                  ))}
                </select>
                <User className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
              </div>
              
              {/* Project Filter */}
              <div className="relative">
                <select
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="pl-7 pr-3 py-1.5 rounded-lg text-xs font-medium appearance-none cursor-pointer"
                  style={{ 
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    color: '#475569'
                  }}
                  data-testid="filter-project"
                >
                  <option value="">All Projects</option>
                  {uniqueProjects.map(project => (
                    <option key={project} value={project}>{project}</option>
                  ))}
                </select>
                <FolderKanban className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
              </div>
            </div>
          </div>

          {/* Notification Cards */}
          <div className="space-y-3">
            {filteredNotifications.length === 0 ? (
              <div 
                className="rounded-xl p-12 text-center"
                style={{ 
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0'
                }}
              >
                <InboxIcon className="w-12 h-12 mx-auto mb-3" style={{ color: '#CBD5E1' }} />
                <h3 className="text-lg font-semibold mb-1" style={{ color: '#475569' }}>
                  No notifications
                </h3>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  {selectedCategory !== 'ALL' 
                    ? `No ${CATEGORIES.find(c => c.id === selectedCategory)?.name.toLowerCase()} yet`
                    : 'Activity will appear here as your team works'
                  }
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const categoryInfo = NOTIFICATION_CATEGORIES[notification.category];
                return (
                  <div
                    key={notification.id}
                    className="rounded-xl overflow-hidden transition-all hover:shadow-md"
                    style={{ 
                      backgroundColor: notification.read ? '#FFFFFF' : 'rgba(0, 201, 167, 0.03)',
                      border: '1px solid #E2E8F0',
                      borderLeft: `4px solid ${categoryInfo?.color || '#64748B'}`
                    }}
                    data-testid={`inbox-notification-${notification.id}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div 
                          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ 
                            backgroundColor: categoryInfo?.bgColor || '#F1F5F9',
                            color: categoryInfo?.color || '#64748B'
                          }}
                        >
                          {getNotificationIcon(notification.category)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 
                                className="font-semibold text-sm"
                                style={{ color: '#0F172A' }}
                              >
                                {notification.headline}
                              </h4>
                              <p 
                                className="text-sm mt-1 leading-relaxed"
                                style={{ color: '#64748B' }}
                              >
                                {notification.detail}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                              className="flex-shrink-0 p-1.5 rounded-lg transition-all hover:bg-gray-100"
                              style={{ color: '#94A3B8' }}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Meta Info */}
                          <div className="flex items-center gap-3 mt-3">
                            <span 
                              className="text-xs flex items-center gap-1"
                              style={{ color: '#94A3B8' }}
                            >
                              <Clock className="w-3 h-3" />
                              {getRelativeTime(notification.timestamp)}
                            </span>
                            {notification.person && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/team');
                                }}
                                className="text-xs px-2 py-0.5 rounded-full transition-all hover:bg-gray-100"
                                style={{ 
                                  backgroundColor: '#F1F5F9',
                                  color: '#475569'
                                }}
                              >
                                {notification.person}
                              </button>
                            )}
                            {notification.project && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/projects');
                                }}
                                className="text-xs px-2 py-0.5 rounded-full transition-all hover:bg-gray-100"
                                style={{ 
                                  backgroundColor: '#F1F5F9',
                                  color: '#475569'
                                }}
                              >
                                {notification.project}
                              </button>
                            )}
                          </div>

                          {/* Actions */}
                          {notification.actions && notification.actions.length > 0 && (
                            <div className="flex items-center gap-2 mt-3">
                              {notification.actions.map((action, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNotificationClick(notification);
                                    if (action.navigate) navigate(action.navigate);
                                  }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                                  style={action.primary ? {
                                    backgroundColor: categoryInfo?.color || '#64748B',
                                    color: '#FFFFFF'
                                  } : {
                                    backgroundColor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    color: '#475569'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (action.primary) {
                                      e.currentTarget.style.opacity = '0.9';
                                    } else {
                                      e.currentTarget.style.backgroundColor = '#F1F5F9';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (action.primary) {
                                      e.currentTarget.style.opacity = '1';
                                    } else {
                                      e.currentTarget.style.backgroundColor = '#F8FAFC';
                                    }
                                  }}
                                >
                                  {action.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Clear Demo Notifications Button */}
          {notifications.length > 0 && (
            <div className="mt-8 text-center">
              <button
                onClick={clearAllNotifications}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all hover:bg-red-50"
                style={{ 
                  color: '#EF4444',
                  border: '1px solid #FEE2E2'
                }}
                data-testid="clear-demo-notifications"
              >
                <Trash2 className="w-4 h-4" />
                Clear Demo Notifications
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
